import React, { useEffect, useState } from "react";
import axios from "axios";
import { Scale, ShieldCheck, AlertTriangle, RefreshCw, TrendingDown } from "lucide-react";
import { API } from "@/lib/api";

const client = axios.create({ baseURL: API, timeout: 30000, withCredentials: true });

const fmt$ = (n) =>
  n == null
    ? "—"
    : "$" + Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * MarginForecastPanel — Reg-T vs Portfolio Margin side-by-side for the
 * current Forge strategy. Auto-refreshes when legs/spot/iv change.
 */
export default function MarginForecastPanel({ legs, spot, iv, r = 0.045, isIndex = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!legs?.length || !spot) {
      setData(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setErr(null);
    const t = setTimeout(async () => {
      try {
        const res = await client.post(`/forge/margin`, {
          legs, spot, iv, r, is_index: isIndex,
        });
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Margin forecast failed");
      } finally {
        if (alive) setLoading(false);
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [JSON.stringify(legs), spot, iv, r, isIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!legs?.length) {
    return (
      <div
        className="bg-[#0c0c0e] border border-white/10 rounded-xl p-4 sm:p-5"
        data-testid="margin-forecast-panel"
      >
        <div className="flex items-center gap-2 mb-2">
          <Scale className="w-4 h-4 text-[#3FE5DC]" />
          <h3 className="text-sm font-medium tracking-tight">Live Margin Forecast</h3>
        </div>
        <p className="text-[11px] text-white/45 italic">
          Build a strategy to see Reg-T vs Portfolio Margin requirements.
        </p>
      </div>
    );
  }

  return (
    <div
      className="bg-[#0c0c0e] border border-white/10 rounded-xl p-4 sm:p-5"
      data-testid="margin-forecast-panel"
    >
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Scale className="w-4 h-4 text-[#3FE5DC]" />
          <h3 className="text-sm font-medium tracking-tight">
            Live Margin Forecast
          </h3>
          {loading && <RefreshCw className="w-3 h-3 animate-spin text-white/40" />}
        </div>
        {data?.is_index && (
          <span className="text-[9px] uppercase tracking-[0.18em] text-[#A98CFF] border border-[#A98CFF]/40 bg-[#A98CFF]/5 rounded px-1.5 py-0.5">
            Index · ±20% shock
          </span>
        )}
      </div>

      {err && (
        <div className="text-[11px] text-[#FF3B30] bg-[#FF3B30]/5 border border-[#FF3B30]/30 rounded px-2 py-1.5 mb-3">
          {err}
        </div>
      )}

      {!data ? (
        <p className="text-[11px] text-white/40">Computing…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            <RegtCard value={data.regt.regt_initial_margin} />
            <PmCard value={data.pm.portfolio_margin} />
            <SavedCard
              saved={data.buying_power_saved}
              savedPct={data.buying_power_saved_pct}
            />
          </div>

          {/* PM eligibility note */}
          <div
            className="rounded-md border border-white/10 bg-[#0a0a0c] px-3 py-2 text-[11px] text-white/55 mb-3 flex items-start gap-2"
            data-testid="margin-pm-note"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-[#3FE5DC] mt-0.5" />
            <span>
              Portfolio Margin requires{" "}
              <strong className="text-white">
                ${data.pm_eligible_min_equity.toLocaleString()}+
              </strong>{" "}
              equity. PM savings shown assume an approved account; standard
              accounts use Reg-T.
            </span>
          </div>

          {/* Worst-case PM scenario */}
          {data.pm.worst_scenario && (
            <div
              className="rounded-md border border-[#FF7A33]/30 bg-[#FF7A33]/5 px-3 py-2 text-[11px] mb-3"
              data-testid="margin-worst-case"
            >
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingDown className="w-3.5 h-3.5 text-[#FF7A33]" />
                <span className="uppercase tracking-[0.2em] text-[9px] text-[#FF7A33] font-medium">
                  PM Binding Scenario
                </span>
              </div>
              <div className="text-white/75 leading-snug">
                Spot{" "}
                <span className="num text-[#FF7A33]">
                  {data.pm.worst_scenario.price_shock_pct >= 0 ? "+" : ""}
                  {data.pm.worst_scenario.price_shock_pct}%
                </span>{" "}
                · IV{" "}
                <span className="num text-[#FF7A33]">
                  {data.pm.worst_scenario.vol_shock_abs >= 0 ? "+" : ""}
                  {data.pm.worst_scenario.vol_shock_abs} pts
                </span>{" "}
                → loss{" "}
                <span className="num text-[#FF3B30] font-medium">
                  {fmt$(data.pm.worst_scenario.pnl)}
                </span>{" "}
                @ ${data.pm.worst_scenario.shocked_spot}
              </div>
            </div>
          )}

          {/* Reg-T breakdown */}
          <details className="text-[11px]" data-testid="margin-breakdown-toggle">
            <summary className="cursor-pointer text-white/55 hover:text-white">
              Reg-T breakdown ({data.regt.breakdown.length} rows)
            </summary>
            <div className="mt-2 rounded-lg border border-white/10 overflow-hidden">
              <table className="w-full text-[11px]">
                <thead className="bg-white/[0.02]">
                  <tr className="text-white/40 uppercase tracking-[0.16em] text-[9px]">
                    <th className="text-left px-3 py-1.5">Leg</th>
                    <th className="text-left px-3 py-1.5">Rule</th>
                    <th className="text-right px-3 py-1.5">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {data.regt.breakdown.map((row, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="px-3 py-1.5 text-white/80">{row.leg_desc}</td>
                      <td className="px-3 py-1.5 text-white/55">{row.rule}</td>
                      <td className="px-3 py-1.5 text-right num text-[#FFD60A]">
                        {fmt$(row.margin)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          {/* Educational footer */}
          <div className="mt-3 text-[10px] text-white/45 italic leading-relaxed">
            <AlertTriangle className="w-3 h-3 inline-block mr-1 text-white/35" />
            PM uses TIMS array-of-shocks across {data.pm.shock_range_pct === 20 ? "±20%" : "±15%"} spot
            × ±4 vol points (worst case wins). Reg-T uses rule-based formulas:
            naked = max(20% S − OTM, 10% strike) + premium; spreads = width − credit.
          </div>
        </>
      )}
    </div>
  );
}

const RegtCard = ({ value }) => (
  <div
    className="bg-[#0a0a0c] border border-[#FFD60A]/30 rounded-lg px-3 py-2.5"
    data-testid="margin-regt-card"
  >
    <div className="text-[9px] uppercase tracking-[0.22em] text-[#FFD60A]">Reg-T</div>
    <div className="num text-lg font-medium text-[#FFD60A] mt-1">{fmt$(value)}</div>
    <div className="text-[9px] text-white/45 mt-0.5">Standard account</div>
  </div>
);

const PmCard = ({ value }) => (
  <div
    className="bg-[#0a0a0c] border border-[#3FE5DC]/30 rounded-lg px-3 py-2.5"
    data-testid="margin-pm-card"
  >
    <div className="text-[9px] uppercase tracking-[0.22em] text-[#3FE5DC]">Portfolio Margin</div>
    <div className="num text-lg font-medium text-[#3FE5DC] mt-1">{fmt$(value)}</div>
    <div className="text-[9px] text-white/45 mt-0.5">$125k+ accounts</div>
  </div>
);

const SavedCard = ({ saved, savedPct }) => (
  <div
    className="bg-[#0a0a0c] border border-[#00C805]/30 rounded-lg px-3 py-2.5 col-span-2 sm:col-span-1"
    data-testid="margin-saved-card"
  >
    <div className="text-[9px] uppercase tracking-[0.22em] text-[#00C805]">BP Saved (PM)</div>
    <div className="num text-lg font-medium text-[#00C805] mt-1">
      {saved >= 0 ? `+${fmt$(saved)}` : fmt$(saved)}
    </div>
    <div className="text-[9px] text-white/45 mt-0.5">
      {savedPct >= 0 ? `+${savedPct}%` : `${savedPct}%`} BP freed
    </div>
  </div>
);
